export type RouteName =
  | "activity"
  | "dashboard"
  | "new-agent"
  | "not-found"
  | "repo-routines"
  | "repo-settings"
  | "repo"
  | "runners"
  | "settings"
  | "task";

export type RouteInfo = {
  readonly name: RouteName;
  readonly repoId?: string;
  // Selected sub-element within a task tab (e.g. a service or shell), slugged.
  readonly sub?: string;
  // Active tab within a task workspace.
  readonly tab?: string;
  readonly taskLabel?: string;
};

export const paths = {
  activity: (): string => "/activity",
  dashboard: (): string => "/repos",
  newAgent: (repoId?: string): string =>
    repoId ? `/repos/${repoId}/new` : "/new",
  repo: (repoId: string): string => `/repos/${repoId}`,
  repoRoutines: (repoId: string): string => `/repos/${repoId}/routines`,
  repoSettings: (repoId: string): string => `/repos/${repoId}/settings`,
  runners: (): string => "/runners",
  settings: (): string => "/settings",
  task: (
    repoId: string,
    taskLabel: string,
    tab?: string,
    sub?: string,
  ): string => {
    const base = `/repos/${repoId}/tasks/${taskLabel}`;
    if (tab === undefined) {
      return base;
    }
    return sub === undefined ? `${base}/${tab}` : `${base}/${tab}/${sub}`;
  },
};

/** Derive a structured route descriptor from a pathname. */
export const parseRoute = (pathname: string): RouteInfo => {
  const p = pathname
    .replace(/^\/+|\/+$/g, "")
    .split("/")
    .filter(Boolean);
  if (p.length === 0 || p[0] === "repos") {
    if (p.length <= 1) {
      return { name: "dashboard" };
    }
    const repoId = p[1];
    if (p[2] === "new") {
      return { name: "new-agent", repoId };
    }
    if (p[2] === "routines") {
      return { name: "repo-routines", repoId };
    }
    if (p[2] === "settings") {
      return { name: "repo-settings", repoId };
    }
    if (p[2] === "tasks" && p[3]) {
      return { name: "task", repoId, sub: p[5], tab: p[4], taskLabel: p[3] };
    }
    return { name: "repo", repoId };
  }
  if (p[0] === "activity") {
    return { name: "activity" };
  }
  if (p[0] === "runners") {
    return { name: "runners" };
  }
  if (p[0] === "settings") {
    return { name: "settings" };
  }
  if (p[0] === "new") {
    return { name: "new-agent" };
  }
  return { name: "not-found" };
};
